import { useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import { supabase } from '../utils/supabase';

import CourseModal from '../components/CourseModal';

import DeleteIconButton from '../components/DeleteIconButton';

import RenderMonitor from '../components/RenderMonitor';

import { COURSES_CACHE_KEY, invalidateAdminCache, readCache, writeCache } from '../utils/adminCache';



function AdminCoursesPage() {

  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);

  const [fetching, setFetching] = useState(true);

  const [fetchError, setFetchError] = useState('');

  const [showCourseModal, setShowCourseModal] = useState(false);

  const [courseToDelete, setCourseToDelete] = useState(null);

  const [deleting, setDeleting] = useState(false);

  const [deleteError, setDeleteError] = useState('');



  useEffect(() => {

    const fetchData = async () => {

      setFetching(true);

      setFetchError('');

      const cachedCourses = readCache(COURSES_CACHE_KEY);

      if (cachedCourses) {

        setCourses((cachedCourses || []).filter((c) => c.active !== false));

        setFetching(false);

        return;

      }



      const { data: coursesData, error: coursesError } = await supabase

        .from('courses')

        .select('id, name, active');



      if (coursesError) {

        setFetchError(coursesError.message);

      } else {

        writeCache(COURSES_CACHE_KEY, coursesData || []);

        setCourses((coursesData || []).filter((c) => c.active !== false));

      }

      setFetching(false);

    };

    fetchData();

  }, []);



  const handleDeleteCourse = (course) => {

    setCourseToDelete(course);

    setDeleteError('');

  };



  const confirmDeleteCourse = async () => {

    if (!courseToDelete) return;

    setDeleting(true);

    setDeleteError('');

    try {

      const { data: courseUsage, error } = await supabase

        .from('bootcamps')

        .select('id')

        .eq('course_id', courseToDelete.id)

        .limit(1);

      if (error) {

        setDeleteError('Could not check course usage.');

        setDeleting(false);

        return;

      }

      if (!courseUsage || courseUsage.length === 0) {

        const { error: deleteError } = await supabase

          .from('courses')

          .delete()

          .eq('id', courseToDelete.id);

        if (deleteError) {

          setDeleteError('Could not delete course.');

          setDeleting(false);

          return;

        }

      } else {

        const { error: archiveError } = await supabase

          .from('courses')

          .update({ active: false })

          .eq('id', courseToDelete.id);

        if (archiveError) {

          setDeleteError('Could not archive course.');

          setDeleting(false);

          return;

        }

      }

    } catch {

      setDeleteError('Unexpected error while deleting/archiving course.');

      setDeleting(false);

      return;

    }

    setCourseToDelete(null);

    setDeleting(false);

    invalidateAdminCache();

    window.location.reload();

  };



  if (fetching) {

    return <div className="dashboard-page"><div className="modern-loading">Loading courses…</div></div>;

  }



  if (fetchError) {

    return <div className="dashboard-page"><div className="modern-error">{fetchError}</div></div>;

  }



  return (

    <div className="dashboard-page">

      <RenderMonitor componentName="AdminCoursesPage" />

      <div className="modern-flex modern-justify-between modern-items-center modern-mb-6">

        <div>

          <h1 className="modern-title">Courses</h1>

          <p className="modern-subtitle" style={{ marginBottom: 0 }}>{courses.length} active course{courses.length !== 1 ? 's' : ''}</p>

        </div>

        <button onClick={() => setShowCourseModal(true)} className="modern-button modern-button-brand">

          + Add Course

        </button>

      </div>



      <div className="modern-list-panel">

        {courses.length === 0 ? (

          <div className="modern-list-empty">No courses yet. Add your first course to get started.</div>

        ) : (

          courses.map((course) => (

            <div

              key={course.id}

              className="modern-list-row modern-list-row-clickable modern-list-row-with-action"

              onClick={() => navigate(`/course/${course.id}`, { state: { courseName: course.name || '' } })}

            >

              <span className="modern-list-row-label">{course.name || 'Untitled Course'}</span>

              <DeleteIconButton

                title="Delete course"

                onClick={(e) => {

                  e.stopPropagation();

                  handleDeleteCourse(course);

                }}

              />

            </div>

          ))

        )}

      </div>



      <div className="modern-list-footer">

        <button

          className="modern-button modern-button-secondary"

          onClick={() => navigate('/archived-courses')}

        >

          Archived Courses

        </button>

      </div>



      <CourseModal

        open={showCourseModal}

        onClose={() => setShowCourseModal(false)}

        onAdd={(id) => {

          invalidateAdminCache();

          setShowCourseModal(false);

          navigate(`/course/${id}`, { state: { courseName: '' } });

        }}

        existingTitles={courses.map((c) => c.name || '').filter(Boolean)}

      />



      {courseToDelete && (

        <div className="modern-modal-overlay">

          <div className="modern-modal">

            <button onClick={() => { setCourseToDelete(null); setDeleteError(''); }} className="modern-modal-close">&times;</button>

            <h2 className="modern-heading">Are you sure you want to delete this course?</h2>

            <p style={{ color: '#64748b', marginBottom: 24 }}>

              {deleting ? 'Processing...' : 'If this course has been used before, it will be archived instead of deleted.'}

            </p>

            {deleteError && <div className="modern-error">{deleteError}</div>}

            <div className="modern-flex modern-gap-4">

              <button className="modern-button modern-button-danger" onClick={confirmDeleteCourse} disabled={deleting}>

                {deleting ? 'Deleting...' : 'Delete Course'}

              </button>

              <button className="modern-button modern-button-secondary" onClick={() => { setCourseToDelete(null); setDeleteError(''); }} disabled={deleting}>

                Cancel

              </button>

            </div>

          </div>

        </div>

      )}

    </div>

  );

}



export default AdminCoursesPage;

